// Screenshot capture utility using html2canvas
// Note: html2canvas needs to be installed: npm install html2canvas @types/html2canvas

export interface ScreenshotOptions {
  quality?: number; // 0-1, only for JPEG
  format?: "png" | "jpeg" | "webp";
  maxWidth?: number;
  maxHeight?: number;
  excludeElements?: string[]; // CSS selectors to exclude
  backgroundColor?: string;
  scale?: number;
}

export interface ScreenshotResult {
  dataUrl: string;
  width: number;
  height: number;
  format: string;
  size: number; // Approximate size in bytes
}

export class ScreenshotCapture {
  private static html2canvas: any = null;

  /**
   * Dynamically import html2canvas to avoid SSR issues
   */
  private static async loadHtml2Canvas(): Promise<any> {
    if (this.html2canvas) {
      return this.html2canvas;
    }

    try {
      // Dynamic import to avoid SSR issues
      const html2canvasModule = await import("html2canvas");
      this.html2canvas = html2canvasModule.default || html2canvasModule;
      return this.html2canvas;
    } catch (error) {
      console.error("Failed to load html2canvas:", error);
      throw new Error("Screenshot capture library not available");
    }
  }

  /**
   * Capture a screenshot of the current page
   */
  static async captureScreenshot(
    options: ScreenshotOptions = {},
  ): Promise<string> {
    const {
      quality = 0.8,
      format = "jpeg",
      maxWidth = 1920,
      maxHeight = 1080,
      excludeElements = [],
      backgroundColor = "#ffffff",
      scale = 1,
    } = options;

    try {
      // Check if we're in a browser environment
      if (typeof window === "undefined" || typeof document === "undefined") {
        throw new Error(
          "Screenshot capture is only available in browser environment",
        );
      }

      // Load html2canvas dynamically
      const html2canvas = await this.loadHtml2Canvas();

      // Prepare elements to exclude
      const excludedElements = new Set<HTMLElement>();
      excludeElements.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el) => excludedElements.add(el as HTMLElement));
      });

      // Configure html2canvas options
      const html2canvasOptions = {
        backgroundColor,
        scale,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: true,
        imageTimeout: 15000,
        removeContainer: true,
        logging: false,
        width: Math.min(window.innerWidth, maxWidth),
        height: Math.min(window.innerHeight, maxHeight),
        scrollX: 0,
        scrollY: 0,
        ignoreElements: (element: HTMLElement) => {
          // Exclude elements with data attribute
          if (element.hasAttribute("data-bug-report-exclude")) {
            return true;
          }

          // Exclude elements by selector
          return excludedElements.has(element);
        },
      };

      // Capture the screenshot
      const canvas = await html2canvas(document.body, html2canvasOptions);

      // Convert canvas to data URL with specified format
      let dataUrl: string;
      if (format === "jpeg") {
        dataUrl = canvas.toDataURL("image/jpeg", quality);
      } else if (format === "webp") {
        dataUrl = canvas.toDataURL("image/webp", quality);
      } else {
        dataUrl = canvas.toDataURL("image/png");
      }

      // Validate the result
      if (!dataUrl || dataUrl === "data:,") {
        throw new Error("Failed to generate screenshot data");
      }

      return dataUrl;
    } catch (error) {
      console.error("Screenshot capture failed:", error);

      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes("html2canvas")) {
          throw new Error(
            "Screenshot library failed to load. Please ensure html2canvas is installed.",
          );
        }
        if (error.message.includes("canvas")) {
          throw new Error(
            "Canvas generation failed. This may be due to CORS restrictions or browser limitations.",
          );
        }
        throw error;
      }

      throw new Error("Screenshot capture failed for unknown reason");
    }
  }

  /**
   * Capture screenshot with automatic retry logic
   */
  static async captureScreenshotWithRetry(
    options: ScreenshotOptions = {},
    maxRetries: number = 3,
    retryDelay: number = 1000,
  ): Promise<string> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await this.captureScreenshot(options);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error("Unknown error");
        console.warn(
          `Screenshot attempt ${attempt}/${maxRetries} failed:`,
          lastError.message,
        );

        // Don't retry on certain types of errors
        if (
          lastError.message.includes("not available") ||
          lastError.message.includes("browser environment")
        ) {
          break;
        }

        // Wait before retrying (except on last attempt)
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    }

    throw lastError || new Error("Screenshot capture failed after all retries");
  }

  /**
   * Get screenshot info without actually capturing
   */
  static getScreenshotInfo(): { supported: boolean; reason?: string } {
    // Check browser environment
    if (typeof window === "undefined" || typeof document === "undefined") {
      return { supported: false, reason: "Not in browser environment" };
    }

    // Check canvas support
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return { supported: false, reason: "Canvas not supported" };
      }
    } catch {
      return { supported: false, reason: "Canvas creation failed" };
    }

    // Check if html2canvas can be loaded (this is async, so we can't check here)
    return { supported: true };
  }

  /**
   * Convert data URL to approximate file size in bytes
   */
  static getDataUrlSize(dataUrl: string): number {
    try {
      // Remove data URL prefix
      const base64 = dataUrl.split(",")[1];
      if (!base64) return 0;

      // Calculate size from base64 length
      // Base64 encoding increases size by ~33%, so actual size is ~75% of base64 length
      return Math.floor((base64.length * 3) / 4);
    } catch {
      return 0;
    }
  }

  /**
   * Compress screenshot if it's too large
   */
  static async compressScreenshot(
    dataUrl: string,
    maxSizeBytes: number = 2 * 1024 * 1024, // 2MB default
  ): Promise<string> {
    const currentSize = this.getDataUrlSize(dataUrl);

    if (currentSize <= maxSizeBytes) {
      return dataUrl;
    }

    try {
      // Create canvas from data URL
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        throw new Error("Canvas context not available");
      }

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = dataUrl;
      });

      // Calculate compression ratio
      const compressionRatio = Math.sqrt(maxSizeBytes / currentSize);
      canvas.width = Math.floor(img.width * compressionRatio);
      canvas.height = Math.floor(img.height * compressionRatio);

      // Draw compressed image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Try different quality levels
      const qualities = [0.7, 0.5, 0.3, 0.1];
      for (const quality of qualities) {
        const compressed = canvas.toDataURL("image/jpeg", quality);
        if (this.getDataUrlSize(compressed) <= maxSizeBytes) {
          return compressed;
        }
      }

      // If still too large, return the lowest quality version
      return canvas.toDataURL("image/jpeg", 0.1);
    } catch (error) {
      console.warn("Screenshot compression failed:", error);
      return dataUrl; // Return original if compression fails
    }
  }

  /**
   * Check if screenshot capture is available and working
   */
  static async isAvailable(): Promise<boolean> {
    try {
      await this.loadHtml2Canvas();
      return this.getScreenshotInfo().supported;
    } catch {
      return false;
    }
  }
}

// Export for backwards compatibility
export const captureScreenshot =
  ScreenshotCapture.captureScreenshot.bind(ScreenshotCapture);
export const isScreenshotAvailable =
  ScreenshotCapture.isAvailable.bind(ScreenshotCapture);
